import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, PencilRuler, Pencil, Trash2, ArrowLeft, Layers3, MapPin, ArrowUpRight, Copy } from "lucide-react";
import api from "@/lib/api";
import { DRAWING_FACES, FACE_SHORT } from "@/lib/designs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Designs({ projectId }) {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState(null);
  const [selected, setSelected] = useState(null); // design detail with drawings
  const [designModal, setDesignModal] = useState(null); // {mode:'new'|'edit', data}
  const [drawingModal, setDrawingModal] = useState(false);
  const [toDeleteDesign, setToDeleteDesign] = useState(null);
  const [toDeleteDrawing, setToDeleteDrawing] = useState(null);
  const [renameDrawing, setRenameDrawing] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const loadDesigns = () =>
    api.get(`/projects/${projectId}/designs`).then((r) => setDesigns(r.data)).catch(() => toast.error("No se pudieron cargar los diseños."));

  useEffect(() => {
    loadDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const openDesign = async (d) => {
    try {
      const r = await api.get(`/projects/${projectId}/designs/${d.design_id}`);
      setSelected(r.data);
    } catch {
      toast.error("No se pudo abrir el diseño.");
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await api.get(`/projects/${projectId}/designs/${selected.design_id}`);
    setSelected(r.data);
  };

  const duplicateDrawing = async (dw) => {
    try {
      await api.post(`/projects/${projectId}/designs/${selected.design_id}/drawings/${dw.drawing_id}/duplicate`);
      toast.success("Dibujo duplicado.");
      refreshSelected();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo duplicar el dibujo.");
    }
  };

  const openRename = (dw) => { setRenameDrawing(dw); setRenameValue(dw.name || ""); };
  const submitRename = async () => {
    const name = renameValue.trim();
    if (!name) return toast.error("El nombre no puede estar vacío.");
    try {
      await api.put(`/projects/${projectId}/designs/${selected.design_id}/drawings/${renameDrawing.drawing_id}`, { name });
      toast.success("Dibujo renombrado.");
      setRenameDrawing(null);
      refreshSelected();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renombrar.");
    }
  };

  // ---------- list view ----------
  if (!selected) {
    return (
      <div data-testid="designs-list">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="caption mb-1">Diseños del proyecto</div>
            <p className="text-sm text-muted-foreground">Cada diseño agrupa uno o varios dibujos (plantas, alzados, etc.).</p>
          </div>
          <Button className="rounded-full gap-2" onClick={() => setDesignModal({ mode: "new", data: { name: "", description: "" } })} data-testid="new-design-button">
            <Plus className="h-4 w-4" /> Nuevo diseño
          </Button>
        </div>

        {!designs ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : designs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center" data-testid="designs-empty">
            <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
              <PencilRuler className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold">Todavía no hay diseños</h3>
            <p className="text-sm text-muted-foreground mt-1">Crea el primero para empezar a dibujar tu vivienda.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {designs.map((d, i) => (
              <div key={d.design_id} className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all" data-testid={`design-card-${i}`}>
                <button onClick={() => openDesign(d)} className="block w-full text-left" data-testid={`open-design-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-lg font-semibold tracking-tight truncate">{d.name}</h3>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                  </div>
                  {d.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.description}</p>}
                  <Badge variant="outline" className="mt-3 gap-1.5 font-normal text-muted-foreground">
                    <Layers3 className="h-3 w-3" /> {d.drawings_count} {d.drawings_count === 1 ? "dibujo" : "dibujos"}
                  </Badge>
                </button>
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border/60">
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => setDesignModal({ mode: "edit", data: d })} data-testid={`edit-design-${i}`} aria-label="Editar">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDeleteDesign(d)} data-testid={`delete-design-${i}`} aria-label="Eliminar">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DesignModal
          projectId={projectId}
          state={designModal}
          onClose={() => setDesignModal(null)}
          onSaved={() => { setDesignModal(null); loadDesigns(); }}
        />
        <ConfirmDelete
          open={Boolean(toDeleteDesign)}
          title={`¿Eliminar el diseño «${toDeleteDesign?.name}»?`}
          description="Se eliminarán también todos sus dibujos. Esta acción no se puede deshacer."
          onCancel={() => setToDeleteDesign(null)}
          onConfirm={async () => {
            try {
              await api.delete(`/projects/${projectId}/designs/${toDeleteDesign.design_id}`);
              toast.success("Diseño eliminado.");
              loadDesigns();
            } catch { toast.error("No se pudo eliminar."); }
            finally { setToDeleteDesign(null); }
          }}
        />
      </div>
    );
  }

  // ---------- design detail (drawings) ----------
  const drawings = selected.drawings || [];
  return (
    <div data-testid="design-detail">
      <button onClick={() => { setSelected(null); loadDesigns(); }} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="designs-back">
        <ArrowLeft className="h-4 w-4" /> Volver a diseños
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold tracking-tight truncate">{selected.name}</h2>
          {selected.description && <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>}
        </div>
        <Button className="rounded-full gap-2" onClick={() => setDrawingModal(true)} data-testid="new-drawing-button">
          <Plus className="h-4 w-4" /> Nuevo dibujo
        </Button>
      </div>

      {drawings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center" data-testid="drawings-empty">
          <div className="mx-auto h-12 w-12 rounded-xl bg-accent text-olive flex items-center justify-center mb-4">
            <PencilRuler className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold">Sin dibujos todavía</h3>
          <p className="text-sm text-muted-foreground mt-1">Añade un dibujo (2D) y elige sobre qué cara dibujar.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {drawings.map((dw, i) => (
            <div key={dw.drawing_id} className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all" data-testid={`drawing-card-${i}`}>
              <button
                onClick={() => navigate(`/admin/proyectos/${projectId}/disenos/${selected.design_id}/dibujo/${dw.drawing_id}`)}
                className="block w-full text-left"
                data-testid={`open-drawing-${i}`}
              >
                <div className="aspect-[16/10] rounded-xl overflow-hidden bg-white border border-border/60 mb-3 flex items-center justify-center" data-testid={`drawing-thumb-${i}`}>
                  {dw.thumbnail ? (
                    <img src={dw.thumbnail} alt={dw.name} className="w-full h-full object-contain" />
                  ) : (
                    <PencilRuler className="h-6 w-6 text-muted-foreground/40" />
                  )}
                </div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-base font-semibold truncate">{dw.name}</h3>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="outline" className="font-mono font-normal uppercase text-[10px]">{dw.mode}</Badge>
                  <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {FACE_SHORT[dw.face] || dw.face}
                  </Badge>
                </div>
              </button>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border/60">
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openRename(dw)} data-testid={`rename-drawing-${i}`} aria-label="Renombrar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => duplicateDrawing(dw)} data-testid={`duplicate-drawing-${i}`} aria-label="Duplicar">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDeleteDrawing(dw)} data-testid={`delete-drawing-${i}`} aria-label="Eliminar">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DrawingModal
        projectId={projectId}
        designId={selected.design_id}
        open={drawingModal}
        onClose={() => setDrawingModal(false)}
        onSaved={() => { setDrawingModal(false); refreshSelected(); }}
      />
      <ConfirmDelete
        open={Boolean(toDeleteDrawing)}
        title={`¿Eliminar el dibujo «${toDeleteDrawing?.name}»?`}
        description="Esta acción no se puede deshacer."
        onCancel={() => setToDeleteDrawing(null)}
        onConfirm={async () => {
          try {
            await api.delete(`/projects/${projectId}/designs/${selected.design_id}/drawings/${toDeleteDrawing.drawing_id}`);
            toast.success("Dibujo eliminado.");
            refreshSelected();
          } catch { toast.error("No se pudo eliminar."); }
          finally { setToDeleteDrawing(null); }
        }}
      />

      <Dialog open={Boolean(renameDrawing)} onOpenChange={(v) => { if (!v) setRenameDrawing(null); }}>
        <DialogContent data-testid="rename-drawing-modal">
          <DialogHeader>
            <DialogTitle>Renombrar dibujo</DialogTitle>
            <DialogDescription>Cambia el nombre de este dibujo.</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <Label htmlFor="rename-name" className="text-xs">Nombre</Label>
            <Input
              id="rename-name"
              autoFocus
              data-testid="rename-drawing-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
              className="mt-1.5"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setRenameDrawing(null)}>Cancelar</Button>
            <Button className="rounded-full" onClick={submitRename} data-testid="rename-drawing-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DesignModal({ projectId, state, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (state) setForm({ name: state.data.name || "", description: state.data.description || "" });
  }, [state]);
  if (!state) return null;
  const editing = state.mode === "edit";
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre del diseño.");
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description.trim() || null };
      if (editing) await api.put(`/projects/${projectId}/designs/${state.data.design_id}`, payload);
      else await api.post(`/projects/${projectId}/designs`, payload);
      toast.success(editing ? "Diseño actualizado." : "Diseño creado.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar."); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="design-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar diseño" : "Nuevo diseño"}</DialogTitle>
          <DialogDescription>Un diseño agrupa los dibujos de tu vivienda.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="dsn-name">Nombre *</Label>
            <Input id="dsn-name" data-testid="design-name-input" placeholder="Ej. Casa principal" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dsn-desc">Descripción</Label>
            <Textarea id="dsn-desc" data-testid="design-description-input" rows={3} placeholder="Opcional" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-full" onClick={submit} disabled={saving} data-testid="save-design-button">
            {saving ? "Guardando…" : editing ? "Guardar" : "Crear diseño"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DrawingModal({ projectId, designId, open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", face: "top" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", face: "top" }); }, [open]);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Indica el nombre del dibujo.");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/designs/${designId}/drawings`, { name: form.name.trim(), mode: "2d", face: form.face });
      toast.success("Dibujo creado.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el dibujo."); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="drawing-modal">
        <DialogHeader>
          <DialogTitle>Nuevo dibujo</DialogTitle>
          <DialogDescription>Empezamos en 2D. Elige sobre qué cara quieres dibujar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="drw-name">Nombre *</Label>
            <Input id="drw-name" data-testid="drawing-name-input" placeholder="Ej. Planta baja" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>Cara sobre la que dibujar</Label>
            <Select value={form.face} onValueChange={(v) => setForm((f) => ({ ...f, face: v }))}>
              <SelectTrigger data-testid="drawing-face-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DRAWING_FACES.map((f) => (
                  <SelectItem key={f.value} value={f.value} data-testid={`drawing-face-option-${f.value}`}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">El dibujo se mantiene en esta cara hasta que se pase a 3D (próximamente).</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-full" onClick={submit} disabled={saving} data-testid="save-drawing-button">
            {saving ? "Creando…" : "Crear dibujo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDelete({ open, title, description, onCancel, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
          <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={onConfirm} data-testid="confirm-delete-button">Eliminar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
